import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  Button,
  Stack,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import ImportFlagRulesModal from './ImportFlagRulesModal';

const IssuesSelectionList = ({ selectedSeverity }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId: paramProjectId } = useParams();
  const { projectId, runId, reportId } = location.state || {};

  const finalProjectId = projectId || paramProjectId;

  const [activeTab, setActiveTab] = useState(0);
  const [issues, setIssues] = useState([]);
  const [totalIssues, setTotalIssues] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [fixedIssues, setFixedIssues] = useState([]);
  const [totalFixedIssues, setTotalFixedIssues] = useState(0);
  const [fixedIssuesLoading, setFixedIssuesLoading] = useState(false);
  const [fixedIssuesPage, setFixedIssuesPage] = useState(0);
  const [fixedIssuesRowsPerPage, setFixedIssuesRowsPerPage] = useState(10);

  // Flagged Issues state
  const [flaggedIssuesList, setFlaggedIssuesList] = useState([]);
  const [totalFlaggedIssues, setTotalFlaggedIssues] = useState(0);
  const [flaggedIssuesLoading, setFlaggedIssuesLoading] = useState(false);
  const [flaggedIssuesPage, setFlaggedIssuesPage] = useState(0);
  const [flaggedIssuesRowsPerPage, setFlaggedIssuesRowsPerPage] = useState(10);

  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);

  // Rules-based flagging state
  const [flaggedRules, setFlaggedRules] = useState(() => {
    const stored = localStorage.getItem('flagged-rules');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  const [csvLoading, setCsvLoading] = useState(false);
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    if (selectedSeverity) {
      setSeverityFilter(selectedSeverity.toUpperCase());
      setPage(0);
    }
  }, [selectedSeverity]);

  const fetchIssues = useCallback(async () => {
    if (!finalProjectId) return;
    setLoading(true);
    try {
      const severityParam = severityFilter !== 'ALL' ? `&severity=${severityFilter}` : '';
      const url = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/sonarqube/issues?project_id=${finalProjectId}${severityParam}&p=${page + 1}&ps=${rowsPerPage}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const data = await response.json();
      setIssues(data.issues || []);
      setTotalIssues(data.total || 0);
    } catch (error) {
      console.error('Error fetching issues:', error);
    } finally {
      setLoading(false);
    }
  }, [finalProjectId, page, rowsPerPage, severityFilter, token]);

  const fetchFixedIssues = useCallback(async () => {
    if (!finalProjectId) return;
    setFixedIssuesLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/sonarqube/solved-issues?project_id=${finalProjectId}&page_size=${fixedIssuesRowsPerPage}&get_all=true&page=${fixedIssuesPage + 1}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const data = await response.json();
      setFixedIssues(data.solved_issues || []);
      setTotalFixedIssues(data.total_count || 0);
    } catch (error) {
      console.error('Error fetching fixed issues:', error);
    } finally {
      setFixedIssuesLoading(false);
    }
  }, [finalProjectId, fixedIssuesPage, fixedIssuesRowsPerPage, token]);

  const fetchFlaggedIssues = useCallback(async () => {
    if (!finalProjectId) return;
    setFlaggedIssuesLoading(true);
    try {
      const url = 'https://g23i2ede0i.execute-api.ap-south-1.amazonaws.com/dev/athrv/api/v1/platform/sonarqube/get-flag-issue';
  
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ project_id: finalProjectId }),
      });
  
      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }
  
      const data = await response.json();
      console.log('Flagged issues response:', data);
      
      setFlaggedIssuesList(data.issues || []);
      setTotalFlaggedIssues(data.count || 0);
      
      // **NEW: Sync flaggedRules with backend**
      const backendFlaggedRules = new Set(
        (data.issues || []).map(issue => issue.issue_id || issue.rule)
      );
      setFlaggedRules(backendFlaggedRules);
      
    } catch (error) {
      console.error('Error fetching flagged issues:', error);
    } finally {
      setFlaggedIssuesLoading(false);
    }
  }, [finalProjectId, token]);

  const downloadRulesCSV = async () => {
    if (!finalProjectId) return;
    setCsvLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/sonarqube/athrv-rules-csv`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.athrv_rules_csv_url) {
        const link = document.createElement('a');
        link.href = data.athrv_rules_csv_url;
        link.setAttribute('download', 'athrv_rules.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading rules CSV:', error);
    } finally {
      setCsvLoading(false);
    }
  };

  const handleImportFlagRules = async (importData) => {
    console.log('Import completed with data:', importData);
    
    // Extract all imported issues and add their rules to flaggedRules
    if (importData.projects && Array.isArray(importData.projects)) {
      const newFlaggedRules = new Set(flaggedRules);
      
      importData.projects.forEach((project) => {
        if (project.issues && Array.isArray(project.issues)) {
          project.issues.forEach((issue) => {
            if (issue.issue_id) {
              newFlaggedRules.add(issue.issue_id);
            }
          });
        }
      });
      
      setFlaggedRules(newFlaggedRules);
      console.log('Updated flagged rules:', Array.from(newFlaggedRules));
    }
    
    // Refresh the flagged issues list
    fetchFlaggedIssues();
    
    // Show a success notification (you can add a Snackbar component for this)
    console.log(`Successfully imported ${importData.total_issues || 0} issues from ${importData.total_projects || 0} projects`);
  };
  // Change 1: Update the handleFlagToggle function to use the issue key
  const handleFlagToggle = async (issue, event) => {
  event.stopPropagation();
  
  const rule = issue.rule || issue.issue_id || '';
  const isFlagged = flaggedRules.has(rule);

  // Optimistically update UI
  setFlaggedRules((prev) => {
    const newSet = new Set(prev);
    if (isFlagged) {
      newSet.delete(rule);
    } else {
      newSet.add(rule);
    }
    return newSet;
  });

  const apiUrlFlag = 'https://g23i2ede0i.execute-api.ap-south-1.amazonaws.com/dev/athrv/api/v1/platform/sonarqube/flag-issue';
  const apiUrlUnflag = 'https://g23i2ede0i.execute-api.ap-south-1.amazonaws.com/dev/athrv/api/v1/platform/sonarqube/unflag-issue';

  try {
    if (isFlagged) {
      // Call unflag API - send only rule in body
      const response = await fetch(apiUrlUnflag, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rule }),
      });
      if (!response.ok) {
        throw new Error(`Unflag API failed: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('Unflagged issue response:', data);
      
      // Refresh flagged issues list if on that tab
      if (activeTab === 2) {
        fetchFlaggedIssues();
      }
    } else {
      // Call flag API with full details
      const postBody = {
        rule: issue.rule || issue.issue_id,
        key: issue.key || issue.sonar_issue_key,
        project_id: finalProjectId,
        severity: issue.severity,
        message: issue.message,
      };
      const response = await fetch(apiUrlFlag, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(postBody),
      });
      if (!response.ok) {
        throw new Error(`Flag API failed: ${response.statusText}`);
      }
      const data = await response.json();
      console.log('Flagged issue response:', data);
      
      // Refresh flagged issues list if on that tab
      if (activeTab === 2) {
        fetchFlaggedIssues();
      }
    }
  } catch (error) {
    console.error('Error toggling flag:', error);

    // Rollback UI change on failure
    setFlaggedRules((prev) => {
      const newSet = new Set(prev);
      if (isFlagged) {
        newSet.add(rule);
      } else {
        newSet.delete(rule);
      }
      return newSet;
    });
  }
};

  // Persist flagged rules to localStorage
  useEffect(() => {
    localStorage.setItem('flagged-rules', JSON.stringify(Array.from(flaggedRules)));
  }, [flaggedRules]);

  useEffect(() => {
    if (activeTab === 0) {
      fetchIssues();
    } else if (activeTab === 1) {
      fetchFixedIssues();
    } else if (activeTab === 2) {
      fetchFlaggedIssues();
    }
  }, [fetchIssues, fetchFixedIssues, fetchFlaggedIssues, activeTab]);

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFixedIssuesChangePage = (event, newPage) => setFixedIssuesPage(newPage);

  const handleFixedIssuesChangeRowsPerPage = (event) => {
    setFixedIssuesRowsPerPage(parseInt(event.target.value, 10));
    setFixedIssuesPage(0);
  };

  const handleFlaggedIssuesChangePage = (event, newPage) => setFlaggedIssuesPage(newPage);

  const handleFlaggedIssuesChangeRowsPerPage = (event) => {
    setFlaggedIssuesRowsPerPage(parseInt(event.target.value, 10));
    setFlaggedIssuesPage(0);
  };

  const handleSeverityChange = (event) => {
    setSeverityFilter(event.target.value);
    setPage(0);
  };

  const handleRowClick = (issue) => {
    navigate(`/issues/${finalProjectId}/${issue.key}`, {
      state: {
        issueKey: issue.key,
        message: issue.message,
        textRange: issue.textRange,
      },
    });
  };

  const renderIssuesTable = (issuesList, isLoading, emptyMessage = 'No issues found', isFixedIssues = false, isFlaggedIssues = false) => (
    <>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', height: '200px', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Severity</TableCell>
                <TableCell>Message</TableCell>
                {!isFlaggedIssues && <TableCell>Component</TableCell>}
                {!isFlaggedIssues && <TableCell>Line</TableCell>}
                {isFlaggedIssues && <TableCell>Code Language</TableCell>}
                <TableCell>Flag Issue</TableCell>
                {isFixedIssues && <TableCell>Status</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {issuesList.length > 0 ? (
                issuesList.map((item) => {
                  const issue = isFixedIssues ? item.issue_details : item;
                  const key = isFixedIssues ? item.sonar_issue_key : (item.sonar_issue_key || item.key);
                  const rule = issue.rule || item.issue_id || '';
                  const codeLanguage = item.code_language || '-';

                  const isFlagged = flaggedRules.has(rule);

                  return (
                    <TableRow
                      key={key}
                      hover
                      onClick={() => !isFixedIssues && !isFlaggedIssues && handleRowClick(issue)}
                      sx={{ cursor: (isFixedIssues || isFlaggedIssues) ? 'default' : 'pointer' }}
                    >
                      <TableCell>{issue.severity || item.severity}</TableCell>
                      <TableCell>
                        <Tooltip title={issue.message || item.message} placement="top" arrow>
                          <Typography noWrap>{issue.message || item.message}</Typography>
                        </Tooltip>
                      </TableCell>
                      {!isFlaggedIssues && <TableCell>{issue.component}</TableCell>}
                      {!isFlaggedIssues && <TableCell>{issue.textRange?.startLine || issue.line || '-'}</TableCell>}
                      {isFlaggedIssues && <TableCell>{codeLanguage}</TableCell>}
                      <TableCell>
                      <IconButton
                          onClick={(e) => handleFlagToggle(issue, e)} // Pass full issue, not just rule
                          size="small"
                          sx={{
                            color: isFlagged ? '#f44336' : 'rgba(0, 0, 0, 0.54)',
                            '&:hover': { color: '#f44336' },
                          }}
                          >
                          {isFlagged ? <FlagIcon /> : <OutlinedFlagIcon />}
                        </IconButton>
                      </TableCell>
                      {isFixedIssues && <TableCell>Fixed</TableCell>}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={isFlaggedIssues ? 4 : (isFixedIssues ? 6 : 5)} align="center">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );

  return (
    <div>
      <Paper sx={{ p: 2, mt: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Issues Management</Typography>
          <Stack direction="row" spacing={2}>
            {activeTab === 2 && (
              <Button
              variant="contained"
              sx={{
                backgroundColor: 'rgb(12, 20, 50)',
                '&:hover': {
                  backgroundColor: 'rgba(55, 55, 125, 0.9)',
                },
                color: 'white',
              }}
              onClick={() => {
                console.log('Import Flag Rules button clicked');
                console.log('Current modal state:', importModalOpen);
                setImportModalOpen(true);
              }}
            >
              Import Flag Rules
            </Button>
            )}
            <Button
              variant="contained"
              sx={{
                backgroundColor: 'rgb(12, 20, 50)',
                '&:hover': {
                  backgroundColor: 'rgba(55, 55, 125, 0.9)',
                },
                color: 'white',
              }}
              startIcon={<DownloadIcon />}
              onClick={downloadRulesCSV}
              disabled={csvLoading}
            >
              {csvLoading ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Preparing CSV...
                </>
              ) : (
                'Athrv Rules'
              )}
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '10px' }}>
          {finalProjectId && <Typography variant="body1">Project ID: {finalProjectId}</Typography>}
          {runId && <Typography variant="body1">Run ID: {runId}</Typography>}
          {reportId && <Typography variant="body1">Report ID: {reportId}</Typography>}
        </Box>

        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Issues List" />
          <Tab label="Fixed Issues" />
          <Tab label="Flagged Issues" />
        </Tabs>

        {activeTab === 0 && (
          <>
            <Select value={severityFilter} onChange={handleSeverityChange} displayEmpty sx={{ width: 200, mb: 2 }}>
              <MenuItem value="ALL">All Severities</MenuItem>
              <MenuItem value="BLOCKER">Blocker</MenuItem>
              <MenuItem value="CRITICAL">Critical</MenuItem>
              <MenuItem value="MAJOR">Major</MenuItem>
              <MenuItem value="MINOR">Minor</MenuItem>
              <MenuItem value="INFO">Info</MenuItem>
            </Select>

            {renderIssuesTable(issues, loading)}

            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={totalIssues}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}

        {activeTab === 1 && (
          <>
            {renderIssuesTable(fixedIssues, fixedIssuesLoading, 'No fixed issues found', true)}

            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={totalFixedIssues}
              rowsPerPage={fixedIssuesRowsPerPage}
              page={fixedIssuesPage}
              onPageChange={handleFixedIssuesChangePage}
              onRowsPerPageChange={handleFixedIssuesChangeRowsPerPage}
            />
          </>
        )}

        {activeTab === 2 && (
          <>
            {renderIssuesTable(flaggedIssuesList, flaggedIssuesLoading, 'No flagged issues found', false, true)}

            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={totalFlaggedIssues}
              rowsPerPage={flaggedIssuesRowsPerPage}
              page={flaggedIssuesPage}
              onPageChange={handleFlaggedIssuesChangePage}
              onRowsPerPageChange={handleFlaggedIssuesChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Import Flag Rules Modal */}
      
      <ImportFlagRulesModal
        open={importModalOpen}
        onClose={() => {
          console.log('Modal onClose called');
          setImportModalOpen(false);
        }}
        onImport={handleImportFlagRules}
        currentProjectId={finalProjectId}
      />
    </div>
  );
};

export default IssuesSelectionList;